/**
 * ModelSelector — Custom dropdown for AI model selection
 *
 * Groups models by provider, shows thinking capability badges and credit costs.
 * Replaces native <select> in EditChatbotModal, CreateCustomBotModal, BasicInformationStep.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, Check, Sparkles, Loader2 } from 'lucide-react'
import { cn } from '../../utils/cn'

export interface ModelOption {
  value: string
  label: string
  provider: string
  model: string
  credit_cost: number
  supports_thinking: boolean
}

interface ModelSelectorProps {
  models: ModelOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  loading?: boolean
}

// Readable provider names
const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  'google-gla': 'Google',
  google: 'Google',
  deepseek: 'DeepSeek',
  moonshotai: 'Kimi',
  openrouter: 'OpenRouter',
  cohere: 'Cohere',
}

const providerLabel = (raw: string) =>
  PROVIDER_LABELS[raw.toLowerCase()] ?? raw.charAt(0).toUpperCase() + raw.slice(1)

// Sort order for providers — unlisted go to the end alphabetically
const PROVIDER_ORDER: string[] = ['anthropic', 'openai', 'google-gla', 'google', 'deepseek', 'moonshotai', 'openrouter', 'cohere']

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  models,
  value,
  onChange,
  disabled = false,
  loading = false,
}) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  // Scroll selected item into view when opening
  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[data-active="true"]')
      if (active) {
        active.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [open])

  // Group models by provider, sorted
  const grouped = useMemo(() => {
    const groups: Record<string, ModelOption[]> = {}
    for (const m of models) {
      const key = m.provider.toLowerCase()
      if (!groups[key]) groups[key] = []
      groups[key].push(m)
    }
    // Sort providers
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      const ia = PROVIDER_ORDER.indexOf(a)
      const ib = PROVIDER_ORDER.indexOf(b)
      if (ia !== -1 && ib !== -1) return ia - ib
      if (ia !== -1) return -1
      if (ib !== -1) return 1
      return a.localeCompare(b)
    })
    return sorted
  }, [models])

  const selected = models.find((m) => m.value === value)

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { if (!disabled && !loading) setOpen((o) => !o) }}
        className={cn(
          'w-full h-10 px-3 flex items-center justify-between gap-2',
          'border rounded-lg text-sm bg-background transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring',
          open ? 'border-ring ring-2 ring-ring' : 'border-input',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        disabled={disabled || loading}
      >
        {loading ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading models...
          </span>
        ) : selected ? (
          <span className="flex items-center gap-2 min-w-0 truncate">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex-shrink-0">
              {providerLabel(selected.provider)}
            </span>
            <span className="text-foreground font-medium truncate">{selected.model}</span>
            {selected.supports_thinking && (
              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 flex-shrink-0">
                <Sparkles className="w-3 h-3" />
                <span className="text-xs font-semibold uppercase tracking-wide">Think</span>
              </span>
            )}
            <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">{selected.credit_cost} usage</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Select model...</span>
        )}
        <ChevronDown className={cn(
          'w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform',
          open && 'rotate-180'
        )} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={listRef}
          className={cn(
            'absolute z-50 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg',
            'max-h-72 overflow-y-auto overscroll-contain',
          )}
        >
          {grouped.map(([provider, items], gi) => (
            <div key={provider}>
              {/* Provider header */}
              <div className={cn(
                'px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted sticky top-0',
                gi > 0 && 'border-t border-border',
              )}>
                {providerLabel(provider)}
              </div>

              {/* Model items */}
              {items.map((m) => {
                const isActive = m.value === value
                return (
                  <button
                    key={m.value}
                    type="button"
                    data-active={isActive}
                    onClick={() => {
                      onChange(m.value)
                      setOpen(false)
                    }}
                    className={cn(
                      'w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors',
                      'hover:bg-accent',
                      isActive && 'bg-accent',
                    )}
                  >
                    {/* Check mark */}
                    <span className="w-4 flex-shrink-0">
                      {isActive && <Check className="w-3.5 h-3.5 text-primary" />}
                    </span>

                    {/* Model name */}
                    <span className={cn(
                      'font-mono text-sm truncate',
                      isActive ? 'text-primary font-medium' : 'text-foreground',
                    )}>
                      {m.model}
                    </span>

                    {/* Thinking badge */}
                    {m.supports_thinking && (
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 flex-shrink-0">
                        <Sparkles className="w-2.5 h-2.5" />
                        <span className="text-xs font-semibold uppercase tracking-wide">Think</span>
                      </span>
                    )}

                    {/* Usage */}
                    <span className="ml-auto text-xs text-muted-foreground flex-shrink-0 tabular-nums">
                      {m.credit_cost} usage
                    </span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ModelSelector
